/**
 * ActionFollowUpCard Component - Redesigned action-focused follow-up card
 * Follows Share Load-first design principles
 */

import React, { useState } from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import {
  MoreVert,
  Phone,
  Message,
  CheckCircle,
  Edit,
  Delete,
  Schedule,
} from "@mui/icons-material";
import { format, isToday, isTomorrow } from "date-fns";
import { useNavigate } from "react-router-dom";

/**
 * Status dot component - replaces red borders
 */
const StatusDot = ({ status }) => {
  const colors = {
    overdue: "error.main",
    due: "warning.main",
    pending: "info.main",
  };

  return (
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        backgroundColor: colors[status] || "grey.400",
        flexShrink: 0,
      }}
    />
  );
};

/**
 * Extract next action verb from follow-up
 */
const extractNextAction = (followUp) => {
  const desc = followUp.description?.toLowerCase() || "";
  const title = followUp.title?.toLowerCase() || "";

  // Look for action verbs
  if (desc.includes("call") || title.includes("call")) return "Call customer";
  if (desc.includes("send") || title.includes("send")) return "Send quote";
  if (desc.includes("confirm") || title.includes("confirm")) return "Confirm payment";
  if (desc.includes("schedule") || title.includes("schedule")) return "Schedule move";
  if (desc.includes("collect") || title.includes("collect")) return "Collect deposit";
  if (desc.includes("follow up") || title.includes("follow up")) return "Follow up";

  // Fallback to title or default
  return followUp.title || "Take action";
};

/**
 * Format time label
 */
const formatTime = (dueDate, isCompleted) => {
  if (!dueDate) return "";

  const date = new Date(dueDate);

  if (isCompleted) {
    return format(date, "MMM dd");
  }

  if (isToday(date)) {
    const time = format(date, "HH:mm");
    return `Today · ${time}`;
  }

  if (isTomorrow(date)) {
    return "Tomorrow";
  }

  return format(date, "MMM dd");
};

/**
 * Get primary action button label
 */
const getPrimaryActionLabel = (followUp) => {
  const channel = followUp.entityContext?.channel?.toLowerCase();
  if (channel === "whatsapp") return "Message";
  if (channel === "phone" || channel === "call") return "Call";
  return "View";
};

/**
 * Get entity type display name
 */
const getEntityTypeDisplay = (entityType) => {
  const displayMap = {
    order: "Share Load",
    quote: "Quote",
    customer: "Customer",
    invoice: "Invoice",
    lead: "Lead",
    general: "General",
  };
  return displayMap[entityType] || entityType;
};

/**
 * Get entity-specific details for display
 */
const getEntityDetails = (followUp) => {
  const ctx = followUp.entityContext || {};
  const type = followUp.entityType;

  switch (type) {
    case "quote":
      return [
        ctx.from && ctx.to && `${ctx.from.substring(0, 25)} → ${ctx.to.substring(0, 25)}`,
        ctx.dates?.length > 0 && `Dates: ${ctx.dates.slice(0, 2).join(", ")}`,
        ctx.truckSize && `Truck: ${ctx.truckSize}`,
        ctx.totalCost && `R${ctx.totalCost.toLocaleString()}`,
      ].filter(Boolean);

    case "order":
      return [
        ctx.pickupAddress && `From: ${ctx.pickupAddress.substring(0, 30)}${ctx.pickupAddress.length > 30 ? "..." : ""}`,
        ctx.moveDate && `Move: ${format(new Date(ctx.moveDate), "MMM dd")}`,
        ctx.pickupTimeWindow && `Time: ${ctx.pickupTimeWindow}`,
        ctx.assignedDriver && `Driver: ${ctx.assignedDriver}`,
      ].filter(Boolean);

    case "lead":
      return [
        ctx.leadFrom && ctx.leadTo && `${ctx.leadFrom.substring(0, 20)} → ${ctx.leadTo.substring(0, 20)}`,
        ctx.category && `Type: ${ctx.category.replace(/_/g, " ")}`,
        ctx.budgetRange && `Budget: ${ctx.budgetRange.replace(/_/g, " ")}`,
      ].filter(Boolean);

    case "invoice":
      return [
        ctx.invoiceTotal && `Total: R${ctx.invoiceTotal.toLocaleString()}`,
        ctx.dueDate && `Due: ${format(new Date(ctx.dueDate), "MMM dd")}`,
        ctx.itemsCount > 0 && `${ctx.itemsCount} items`,
      ].filter(Boolean);

    case "customer":
      return [
        ctx.customerAddress && `Address: ${ctx.customerAddress.substring(0, 30)}${ctx.customerAddress.length > 30 ? "..." : ""}`,
        (ctx.leadsCount > 0 || ctx.quotesCount > 0 || ctx.ordersCount > 0) &&
          `${ctx.leadsCount || 0} leads · ${ctx.quotesCount || 0} quotes · ${ctx.ordersCount || 0} orders`,
      ].filter(Boolean);

    default:
      return [];
  }
};

/**
 * ActionFollowUpCard Component
 */
const ActionFollowUpCard = ({
  followUp,
  mode = "needsAction",
  onAction,
  selectMode = false,
  selected = false,
  onToggleSelect,
  actionLoading = false,
}) => {
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState(null);

  const isCompleted = mode === "completed";
  const isOverdue = followUp.status === "overdue";
  const isDueToday = followUp.status === "due";

  // Extract data
  const personName = followUp.entityContext?.name || "Customer";
  const entityType = getEntityTypeDisplay(followUp.entityType || "general");
  const nextAction = !isCompleted ? extractNextAction(followUp) : null;
  const timeLabel = formatTime(followUp.dueDate, isCompleted);
  const reference =
    followUp.entityContext?.referenceNumber ||
    followUp.entityId?.slice(-6) ||
    followUp.id?.slice(-6) ||
    "";

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handlePrimaryAction = async (event) => {
    event.stopPropagation();
    event.preventDefault();

    // Get follow-up ID (support both id and _id for compatibility)
    const followUpId = followUp?.id || followUp?._id;

    // Validate follow-up has an ID
    if (!followUpId) {
      console.error("Follow-up ID is missing", {
        followUp,
        availableKeys: Object.keys(followUp || {}),
      });
      return;
    }

    // If in select mode, toggle selection instead
    if (selectMode && onToggleSelect) {
      onToggleSelect();
      return;
    }

    if (onAction) {
      // Call the async function and handle errors
      try {
        await onAction(followUp, "primary");
      } catch (error) {
        console.error("Error in onAction handler:", error);
      }
    } else {
      navigate(`/followups/${followUpId}`);
    }
  };

  const handleMenuAction = async (action, event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    // Close menu first (Material-UI Menu auto-closes on MenuItem click anyway)
    handleMenuClose();

    // Get follow-up ID for validation
    const followUpId = followUp?.id || followUp?._id;

    if (!followUpId) {
      console.error("Follow-up ID is missing in menu action", {
        followUp,
        action,
        availableKeys: Object.keys(followUp || {}),
      });
      return;
    }

    if (onAction) {
      // Call async action with proper error handling
      try {
        await onAction(followUp, action);
      } catch (error) {
        console.error("Error in menu action handler:", error);
      }
    }
  };

  return (
    <Card
      sx={{
        mb: 1.5,
        borderLeft: isOverdue ? "3px solid" : "none",
        borderColor: isOverdue ? "error.main" : "transparent",
        opacity: isCompleted ? 0.7 : 1,
        backgroundColor: isCompleted ? "grey.50" : "background.paper",
        transition: "all 0.2s ease",
        "&:hover": {
          boxShadow: isCompleted ? 1 : 2,
        },
      }}
    >
      <CardContent sx={{ py: 2 }}>
        {/* Selection and Content Container */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          {selectMode && (
            <Checkbox
              checked={selected}
              onChange={(e) => {
                e.stopPropagation();
                onToggleSelect?.();
              }}
              disabled={actionLoading}
              sx={{ mt: 0.5 }}
            />
          )}

          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Primary Line: Person + Context */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 1,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {personName} — {entityType}
                </Typography>
              </Box>
              {!isCompleted && (
                <Box sx={{ ml: 1, pt: 0.5 }}>
                  <StatusDot status={followUp.status} />
                </Box>
              )}
            </Box>

        {/* Next Action Line */}
        {!isCompleted && nextAction && (
          <Typography
            variant="body2"
            fontWeight={500}
            color="text.primary"
            sx={{ mb: 1 }}
          >
            Next action: {nextAction}
          </Typography>
        )}

        {/* Meta Line */}
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            alignItems: "center",
            mb: 1,
            flexWrap: "wrap",
          }}
        >
          {timeLabel && (
            <Typography variant="caption" color="text.secondary">
              {timeLabel}
            </Typography>
          )}
          {reference && (
            <>
              <Typography variant="caption" color="text.secondary">
                ·
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {entityType} #{reference}
              </Typography>
            </>
          )}
          {followUp.entityContext?.channel && (
            <>
              <Typography variant="caption" color="text.secondary">
                ·
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: "capitalize" }}
              >
                {followUp.entityContext.channel}
              </Typography>
            </>
          )}
        </Box>

        {/* Entity-Specific Details */}
        {getEntityDetails(followUp).length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              mb: 1.5,
            }}
          >
            {getEntityDetails(followUp).map((detail, idx) => (
              <Typography
                key={idx}
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.75rem" }}
              >
                {detail}
              </Typography>
            ))}
          </Box>
        )}

            {/* Actions */}
            {!isCompleted && (
              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                <Button
                  type="button"
                  size="small"
                  variant="contained"
                  onClick={handlePrimaryAction}
                  sx={{ minWidth: 100 }}
                  disabled={actionLoading}
                  startIcon={
                    actionLoading ? <CircularProgress size={14} /> : null
                  }
                >
                  {selectMode
                    ? selected
                      ? "Deselect"
                      : "Select"
                    : getPrimaryActionLabel(followUp)}
                </Button>
                <IconButton
                  size="small"
                  onClick={handleMenuOpen}
                  sx={{ ml: 0.5 }}
                  disabled={actionLoading}
                >
                  <MoreVert fontSize="small" />
                </IconButton>
                <Menu
                  anchorEl={menuAnchor}
                  open={Boolean(menuAnchor)}
                  onClose={handleMenuClose}
                >
                  <MenuItem
                    disabled={actionLoading}
                    onClick={(e) => handleMenuAction("complete", e)}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <CheckCircle fontSize="small" />
                    Mark Done
                  </MenuItem>
                  <MenuItem
                    disabled={actionLoading}
                    onClick={(e) => handleMenuAction("snooze", e)}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Schedule fontSize="small" />
                    Snooze
                  </MenuItem>
                  <MenuItem
                    disabled={actionLoading}
                    onClick={(e) => handleMenuAction("edit", e)}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Edit fontSize="small" />
                    Edit
                  </MenuItem>
                  <MenuItem
                    disabled={actionLoading}
                    onClick={(e) => handleMenuAction("delete", e)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      color: "error.main",
                    }}
                  >
                    <Delete fontSize="small" />
                    Delete
                  </MenuItem>
                </Menu>
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ActionFollowUpCard;

